/**
 * Geolocation Utilities
 * Provides user-friendly error messages and retry logic for geolocation
 */

/**
 * Get user-friendly error message from GeolocationPositionError
 * @param {GeolocationPositionError} error
 * @returns {string} User-friendly error message
 */
export function getGeolocationErrorMessage(error) {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Location access was denied. Please enable location permissions in your browser settings to use this feature.';

    case error.POSITION_UNAVAILABLE:
      return 'Your location is currently unavailable. Please check your device\'s location settings and try again.';

    case error.TIMEOUT:
      return 'Location request timed out. Please try again.';

    default:
      return 'An error occurred while retrieving your location. Please try again.';
  }
}

/**
 * Request geolocation with retry logic and better error handling
 * @param {Object} options - Geolocation options
 * @param {number} retries - Number of retries (default: 2)
 * @returns {Promise<GeolocationPosition>}
 */
export async function requestGeolocation(options = {}, retries = 2) {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
    ...options
  };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser.'));
          return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, defaultOptions);
      });

      return position;
    } catch (error) {
      if (attempt === retries) {
        // Last attempt failed, throw with user-friendly message
        const userMessage = error.code !== undefined
          ? getGeolocationErrorMessage(error)
          : 'Geolocation is not supported by your browser.';

        const enhancedError = new Error(userMessage);
        enhancedError.originalError = error;
        throw enhancedError;
      }

      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
}

/**
 * Check if geolocation is available
 * @returns {boolean}
 */
export function isGeolocationAvailable() {
  return 'geolocation' in navigator;
}

/**
 * Request permission for geolocation (modern browsers)
 * @returns {Promise<PermissionState>}
 */
export async function checkGeolocationPermission() {
  if (!('permissions' in navigator)) {
    return 'prompt'; // Assume prompt if Permissions API not available
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state; // 'granted', 'denied', or 'prompt'
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error checking geolocation permission:', error);
    }
    return 'prompt';
  }
}

/**
 * Get coordinates from geolocation position
 * @param {GeolocationPosition} position
 * @returns {{latitude: number, longitude: number}}
 */
export function extractCoordinates(position) {
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude
  };
}
